// CreateTypeFields.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { MAX_DATABASE_TYPE_LABEL_LENGTH } from 'utilities/databaseTypeLabels';
import { ViewType } from 'utilities/NodeType';
import { getDefaultViewTypeIconKey } from 'utilities/ViewTypeIcons';

// CSS
import './CreateTypeFields.css';
import './CreateTypeSection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Checkbox from 'components/checkbox/Checkbox';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import TypeIcon from 'components/typeicon/TypeIcon';
import AppearAsEditor from 'components/appearas/AppearAsEditor';

/**
 * CreateTypeFields
 * 
 * This component renders the input fields for creating a new node type, including 
 * icon selection, label input, plural label input, description input, and default 
 * view selection.
 * 
 * @param {string} iconKey - The key of the currently selected icon for the type.
 * @param {function} setIconKey - Function to call when the icon selection changes.
 * @param {boolean} isSavingType - Whether the type is currently being saved, used to disable inputs.
 * @param {string} label - The current value of the label input field.
 * @param {function} setLabel - Function to call when the label input changes.
 * @param {string} labelHelperText - Helper text or error message to display below the label input.
 * @param {function} onSubmit - Function to call when the form is submitted (e.g., pressing Enter).
 * @param {string} labelPlural - The current value of the plural label input field.
 * @param {function} onLabelPluralChange - Function to call when the plural label input changes.
 * @param {string} pluralHelperText - Helper text or error message to display below the plural label input.
 * @param {string} description - Optional description for the node type.
 * @param {function} setDescription - Function to call when the description input changes.
 * @param {string} defaultView - The currently selected default view type.
 * @param {function} setDefaultView - Function to call when the default view selection changes.
 * @param {boolean} [showPeekOnOpen=true] - Whether opening a node shows Peek first.
 * @param {function} [setShowPeekOnOpen] - Updates the show-peek-on-open setting.
 * @param {boolean} [allowTopLevelCreate=true] - Whether this type can be created from the space.
 * @param {function} [setAllowTopLevelCreate] - Updates the top-level create setting.
 * @param {boolean} [showInMenu=true] - Whether this type appears in the space Menu.
 * @param {function} [setShowInMenu] - Updates the Menu visibility setting.
 * @param {boolean} [showInAddMenu=true] - Whether this type appears in + Add.
 * @param {function} [setShowInAddMenu] - Updates the Add menu visibility setting.
 * @param {boolean} autoFocusLabel - Whether to auto-focus the label input on mount.
 * 
 * @returns {ReactNode} The rendered component.
 */
const CreateTypeFields = React.memo(({
  iconKey,
  setIconKey,
  isSavingType,
  label,
  setLabel,
  labelHelperText,
  onSubmit,
  labelPlural,
  onLabelPluralChange,
  pluralHelperText,
  onLabelBlur,
  onLabelPluralBlur,
  description,
  setDescription,
  defaultView,
  setDefaultView,
  showPeekOnOpen = true,
  setShowPeekOnOpen,
  allowTopLevelCreate = true,
  setAllowTopLevelCreate,
  showInMenu = true,
  setShowInMenu,
  showInAddMenu = true,
  setShowInAddMenu,
  appearAs = [],
  setAppearAs,
  schemaFields = [],
  hasOverview = false,
  autoFocusLabel = false,
  width = '100%',
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [isEditingPlural, setIsEditingPlural] = useState(false);
  const [isPluralEditHovered, setIsPluralEditHovered] = useState(false);

  const canEditPlural = Boolean(String(label || '').trim());

  // Build options for default view selector with icons
  const defaultViewOptions = [
    { value: ViewType.TABLE, label: 'Table', iconKey: getDefaultViewTypeIconKey(ViewType.TABLE) },
    { value: ViewType.GALLERY, label: 'Gallery', iconKey: getDefaultViewTypeIconKey(ViewType.GALLERY) },
    { value: ViewType.LIST, label: 'List', iconKey: getDefaultViewTypeIconKey(ViewType.LIST) },
    { value: ViewType.ALPHA_LIST, label: 'Alphabetized List', iconKey: getDefaultViewTypeIconKey(ViewType.ALPHA_LIST) },
    { value: ViewType.FEED, label: 'Feed', iconKey: getDefaultViewTypeIconKey(ViewType.FEED) },
    { value: ViewType.CALENDAR, label: 'Calendar', iconKey: getDefaultViewTypeIconKey(ViewType.CALENDAR) },
    { value: ViewType.MAP, label: 'Map', iconKey: getDefaultViewTypeIconKey(ViewType.MAP) },
  ];

  // ----- RENDER ----- //

  return (

    <ModalForm width={width}>

      <ModalField>

        <ModalLabel label="Default Icon" />

        {/* ICON ROW */}
        <div className="create-type-section-icon-row">
          <TypeIcon
            size={52}
            iconKey={iconKey}
            disabled={isSavingType}
            onChange={setIconKey}
          />
        </div>

        <ModalHint
          hint="Choose the default icon users will see for items in this collection."
        />

      </ModalField>

      <ModalField>

        <ModalLabel label="Singular Name" />

        <TextInput
          value={label}
          placeholder="e.g. 'Contact'"
          maxLength={MAX_DATABASE_TYPE_LABEL_LENGTH}
          autoFocus={autoFocusLabel}
          error={Boolean(labelHelperText)}
          helperText={labelHelperText}
          onBlur={onLabelBlur}
          onChange={(e) => setLabel(e.target.value)}
          onEnterPress={onSubmit}
        />

        <ModalHint
          hint="For individual items in this collection."
        />

      </ModalField>

      <ModalField>

        <ModalLabel label="Plural Name" />

        {isEditingPlural ? (

          <TextInput
            value={labelPlural}
            placeholder="e.g. 'Tasks'"
            maxLength={MAX_DATABASE_TYPE_LABEL_LENGTH}
            error={Boolean(pluralHelperText)}
            helperText={pluralHelperText}
            autoFocus
            onBlur={() => {
              onLabelPluralBlur?.();
              setIsEditingPlural(false);
            }}
            onChange={(e) => onLabelPluralChange(e.target.value)}
            onEnterPress={onSubmit}
          />

        ) : (

          <div
            className={`create-type-section-plural-readonly-row ${canEditPlural ? '' : 'no-edit'}`.trim()}
            style={{
              backgroundColor: theme.backgroundMuted,
            }}
          >

            {/* PLURAL READONLY VALUE */}
            <div
              className="create-type-section-plural-readonly-value"
            >
              {labelPlural || 'Not set'}
            </div>

            {/* PLURAL EDIT BUTTON */}
            {canEditPlural && (

              <div
                className="create-type-section-plural-edit-button"
                style={{
                  backgroundColor: isPluralEditHovered ? theme.backgroundHover : theme.backgroundMuted,
                  border: `1px solid ${isPluralEditHovered ? theme.foregroundMuted : 'transparent'}`,
                }}
                aria-label="Edit plural name"
                title="Edit plural name"
                onClick={() => setIsEditingPlural(true)}
                onMouseEnter={() => setIsPluralEditHovered(true)}
                onMouseLeave={() => setIsPluralEditHovered(false)}
              >
                <Icon
                  iconKey="edit"
                  width={20}
                  height={20}
                  color={theme.foregroundMuted}
                />
              </div>

            )}

          </div>

        )}

        <ModalHint
          hint="For groups of items from this collection."
        />

      </ModalField>

      <ModalField>

        <ModalLabel label="Description" />

        <TextInput
          value={description || ''}
          placeholder="Optional short description for this type"
          maxLength={110}
          lines={2}
          disabled={isSavingType}
          multiline
          expandable
          onChange={(e) => setDescription(e.target.value)}
          onEnterPress={onSubmit}
        />

        <ModalHint
          hint="A short explanation can help members choose the right collection or item type."
        />

      </ModalField>

      <ModalField>

        <ModalLabel label="Default View" />

        <SelectInput
          style={{
            width: '100%',
            fontSize: '14px',
          }}
          iconSize={20}
          value={defaultView || ''}
          options={defaultViewOptions}
          placeholder="Select default view"
          disabled={isSavingType || typeof setDefaultView !== 'function'}
          onValueChange={(nextValue) => {
            if (typeof setDefaultView === 'function') {
              setDefaultView(nextValue);
            }
          }}
        />

        <ModalHint
          hint="When base members first view items in this collection, they'll see this view by default."
        />

      </ModalField>

      {typeof setShowPeekOnOpen === 'function' ? (
        <ModalField>

          <Checkbox
            value={showPeekOnOpen !== false}
            size="16"
            label="Show Peek on open"
            onChange={() => {
              if (isSavingType) return;
              setShowPeekOnOpen(!(showPeekOnOpen !== false));
            }}
          />

          <ModalHint
            hint="When off, opening a node of this type goes straight to the full workspace instead of the Peek panel."
          />

        </ModalField>
      ) : null}

      {typeof setAllowTopLevelCreate === 'function' ? (
        <ModalField>

          <Checkbox
            value={allowTopLevelCreate !== false}
            size="16"
            label="Allow top-level create"
            onChange={() => {
              if (isSavingType) return;
              setAllowTopLevelCreate(!(allowTopLevelCreate !== false));
            }}
          />

          <ModalHint
            hint="When off, this type can only be created from a parent (for example line items), not from the space."
          />

        </ModalField>
      ) : null}

      {typeof setShowInMenu === 'function' ? (
        <ModalField>

          <Checkbox
            value={showInMenu !== false}
            size="16"
            label="Show in Menu"
            onChange={() => {
              if (isSavingType) return;
              setShowInMenu(!(showInMenu !== false));
            }}
          />

          <ModalHint
            hint="Show this collection in the space Menu on the left."
          />

        </ModalField>
      ) : null}

      {typeof setShowInAddMenu === 'function' ? (
        <ModalField>

          <Checkbox
            value={showInAddMenu !== false}
            size="16"
            label="Show in Add menu"
            onChange={() => {
              if (isSavingType) return;
              setShowInAddMenu(!(showInAddMenu !== false));
            }}
          />

          <ModalHint
            hint="Show this type in the + Add menu."
          />

        </ModalField>
      ) : null}

      {typeof setAppearAs === 'function' ? (
        <ModalField>
          <AppearAsEditor
            rows={appearAs}
            schemaFields={schemaFields}
            hasOverview={hasOverview}
            disabled={isSavingType}
            onChange={setAppearAs}
          />
        </ModalField>
      ) : null}

    </ModalForm>
  );
});

// Dev Tools
CreateTypeFields.displayName = 'CreateTypeFields';

export default CreateTypeFields;
